// ---------------------------------------------------------------------------
// useUserLocation — React wrapper around the geolocation helper. Surfaces
// status (idle/prompt/requesting/granted/denied/…) so the UI can show
// pre-prompts, errors, and graceful fallbacks (notably on iPhone where
// denied locations cannot be re-prompted from JS — only from Settings).
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import {
  type GeolocationStatus,
  isIOS,
  readGeolocationPermission,
  requestUserLocation,
} from "@/utils/geolocation";
import { isNativeRuntime } from "@/utils/nativeGeolocation";
import type { LngLat } from "@/utils/polyline";

export interface UseUserLocationState {
  coords: LngLat | undefined;
  /** Reported accuracy of `coords`, metres. null = the platform reported none.
   *  R1: a contribution may only auto-resolve when this is a number <= 25, so
   *  null must never be treated as "probably fine".
   *  Kept in lockstep with `coords` — every write to one writes the other. */
  accuracy: number | null;
  /** Date.now() when `coords` was set. Lets a caller notice a stale fix. */
  fixedAt: number | null;
  status: GeolocationStatus;
  message?: string;
  /** True while a request is in flight (native dialog may be open). */
  isRequesting: boolean;
  /** Trigger a one-shot location request. Safe to call from any UI event. */
  request: () => Promise<void>;
  /** True on iOS — UI should show the Settings instructions when denied. */
  isIOS: boolean;
}

const PROMPT_DISMISSED_KEY = "pleasant.locationPromptDismissed";

export const dismissLocationPrompt = () => {
  try { localStorage.setItem(PROMPT_DISMISSED_KEY, "1"); } catch { /* ignore */ }
};
export const wasLocationPromptDismissed = () => {
  try { return localStorage.getItem(PROMPT_DISMISSED_KEY) === "1"; } catch { return false; }
};

export const useUserLocation = (autoRequest = true): UseUserLocationState => {
  const [coords, setCoords] = useState<LngLat | undefined>();
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [fixedAt, setFixedAt] = useState<number | null>(null);
  const [status, setStatus] = useState<GeolocationStatus>("idle");
  const [message, setMessage] = useState<string | undefined>();
  const [isRequesting, setIsRequesting] = useState(false);
  const inFlightRef = useRef(false);

  const request = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsRequesting(true);
    setStatus((s) => (s === "granted" ? s : "requesting"));

    // (#156) Two-stage location load. Was: single high-accuracy fix with
    // 12s timeout, which on iOS cold start meant 5-15 seconds of spinner
    // before the user saw anything. Morgan: "allow location load takes
    // too long."
    //
    // Stage 1 — fast. Low-accuracy + 4s timeout + 5-minute cached-fix
    // allowance. Usually returns instantly (cached) or within 1-2s
    // (low-accuracy network fix). The user sees their pin appear
    // before they finish reading the screen.
    const fast = await requestUserLocation({
      enableHighAccuracy: false,
      timeoutMs: 4_000,
      maximumAgeMs: 300_000,
    });
    if (fast.coords) {
      setCoords(fast.coords);
      // Stage 1 is deliberately enableHighAccuracy:false with a 5-minute cache
      // allowance, so this accuracy is usually poor. Recording it honestly is
      // what stops a submit button resolving from it (R1).
      setAccuracy(fast.accuracy ?? null);
      setFixedAt(Date.now());
      setStatus("granted");
      setMessage(undefined);
      setIsRequesting(false); // unblock UI right away
    }

    // Stage 2 — precise. High-accuracy upgrade. May take 5-15s but the
    // user already has a usable pin from stage 1, so this is silent. When
    // the precise fix arrives we overwrite coords (the difference is
    // usually 10-100m, important for the route's first turn).
    const precise = await requestUserLocation({
      enableHighAccuracy: true,
      timeoutMs: 12_000,
      // Was 60s — far too stale for a navigation app. At 60 mph a minute-old
      // fix is a mile off. Keep it tight so the cached position stays close to
      // where the user actually is (the trip-launch path forces a 0-cache fix
      // on top of this for the start point).
      maximumAgeMs: 15_000,
    });
    if (precise.coords) {
      setCoords(precise.coords);
      setAccuracy(precise.accuracy ?? null);
      setFixedAt(Date.now());
      setStatus("granted");
      setMessage(undefined);
    } else if (!fast.coords) {
      // Neither stage produced a fix — surface the precise stage's
      // status/message so the user knows what went wrong.
      setStatus(precise.status);
      setMessage(precise.message);
    }

    setIsRequesting(false);
    inFlightRef.current = false;
  }, []);

  // Auto-prime: if the user has previously granted permission we can fetch
  // silently. If "prompt", we DO NOT auto-fire on iOS — the prompt only
  // appears in response to a clear user gesture, otherwise it's wasted.
  useEffect(() => {
    if (!autoRequest) return;
    let cancelled = false;
    (async () => {
      const perm = await readGeolocationPermission();
      if (cancelled) return;
      if (perm === "granted") {
        request();
        return;
      }
      if (perm === "denied") {
        setStatus("denied");
        setMessage(
          isNativeRuntime()
            ? "Location is off for Pleasant Routes. To turn it on: open iPhone Settings → Pleasant Routes → Location → While Using the App."
            : isIOS()
              ? "Location is off for this site. To turn it on: open iPhone Settings → Safari → Location → Allow."
              : "Location access is blocked. You can still plan a trip by typing a starting point.",
        );
        return;
      }
      // "unknown" (PLE-35): iOS Safari doesn't expose the Permissions API for
      // geolocation, so readGeolocationPermission can't tell us "already
      // granted" from "never asked" — every returning web visit looked like a
      // fresh one, and the map sat on the Manhattan fallback until the user
      // rediscovered the hidden "Use current location" row by hand. A device
      // that has ever produced a real fix (pleasant.lastLocation, written
      // only after requestUserLocation succeeds — see MapView) has
      // necessarily been granted permission at least once, so silently retry
      // for it. This can't reintroduce the uninvited dialog: getCurrentPosition
      // only shows a system prompt when the OS permission is genuinely
      // undetermined, and a device with a prior fix has already moved past
      // that — it either still has permission (silent success) or had it
      // revoked since (silent "denied", handled like any other failure).
      if (perm === "unknown") {
        let hasPriorFix = false;
        try { hasPriorFix = window.localStorage.getItem("pleasant.lastLocation") != null; } catch { /* ignore */ }
        if (hasPriorFix) {
          request();
          return;
        }
      }
      // prompt / unknown-with-no-prior-fix — surface a "needs prompt" status
      // so the UI can show its own pre-prompt with context, and only call
      // request() in response to a tap. NO auto-fire for a genuinely
      // undetermined permission on any platform: the system permission
      // dialog must only ever appear as confirmation of a button the user
      // just pressed on OUR screen — never uninvited on page load. (Morgan:
      // the popup over the explainer "screams web based." The legacy
      // non-iOS auto-request branch was the culprit.)
      setStatus("prompt");
    })();
    return () => { cancelled = true; };
  }, [autoRequest, request]);

  // Refresh the fix whenever the app returns to the foreground (Morgan:
  // "current location seems to cache too much — it often tries to start
  // routes from my house when I'm not at my house"). The old behavior
  // fetched coords ONCE on mount; a WebView suspended in the background
  // for hours came back with the pin frozen wherever the app was last
  // opened. visibilitychange fires on both web tabs and the native
  // WKWebView foreground transition, so one listener covers both.
  const statusRef = useRef(status);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && statusRef.current === "granted") {
        void request();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [request]);

  return {
    coords,
    accuracy,
    fixedAt,
    status,
    message,
    isRequesting,
    request,
    isIOS: isIOS(),
  };
};
